import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import ProductCard from '../components/ProductCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';

const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [sortBy, setSortBy] = useState('featured');

  useEffect(() => {
    const fetchData = async () => {
      try {
        await api.seedData();
        const [productsData, categoriesData] = await Promise.all([
          api.getProducts(),
          api.getCategories(),
        ]);
        setProducts(productsData);
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredProducts = products
    .filter((product) => {
      if (selectedCategory !== 'all' && product.category_id !== selectedCategory) {
        return false;
      }
      if (product.price < priceRange[0] || product.price > priceRange[1]) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'name':
          return a.title.localeCompare(b.title);
        case 'featured':
        default:
          return b.featured - a.featured;
      }
    });

  return (
    <div data-testid="shop-page" className="pt-20 min-h-screen bg-void">
      {/* Header */}
      <div className="py-16 md:py-24 px-6 md:px-12 lg:px-24 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <span className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-2 block">
            Collection
          </span>
          <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter uppercase text-white">
            Shop All Skullz
          </h1>
          <p className="text-neutral-400 mt-4 max-w-xl">
            Explore our full collection of handcrafted skull art—each piece is one of a kind and crafted with exceptional attention to detail.
          </p>
        </div>
      </div>

      {/* BTO Explanation Banner */}
      <div className="px-4 md:px-12 lg:px-24 pt-6 md:pt-8">
        <div className="max-w-7xl mx-auto">
          <div className="relative overflow-hidden bg-gradient-to-r from-amber-500/20 via-amber-600/10 to-amber-500/20 border border-amber-500/50 rounded-lg p-4 md:p-8">
            {/* Animated glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-500/10 to-transparent animate-pulse" />
            
            <div className="relative flex items-start gap-3 md:gap-6">
              {/* Icon - smaller on mobile */}
              <div className="flex-shrink-0 w-10 h-10 md:w-16 md:h-16 bg-amber-500/20 border border-amber-500/50 rounded-full flex items-center justify-center">
                <span className="text-xl md:text-3xl">🔨</span>
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1 md:mb-2">
                  <h2 className="font-heading text-base md:text-2xl font-bold text-amber-400 uppercase tracking-wide">
                    Built-to-Order
                  </h2>
                  <span className="px-1.5 py-0.5 bg-amber-500 text-black text-[10px] md:text-xs font-bold uppercase rounded animate-pulse">
                    New
                  </span>
                </div>
                <p className="text-neutral-300 text-xs md:text-base leading-relaxed">
                  <strong className="text-amber-400">Every skull is handcrafted just for you.</strong>
                  <span className="hidden sm:inline"> When you place a BTO order, your piece doesn't exist yet—we create it from scratch with your preferences in mind.</span>
                  <span className="sm:hidden"> Click any item to customize your unique skull.</span>
                  <span className="hidden sm:inline"> Click on any item to discover the BTO option.</span>
                </p>
                <p className="text-cyan-400 text-[11px] md:text-sm mt-1.5 md:mt-2 font-semibold">
                  ✓ Every item can be ordered regardless of stock status
                </p>
                <p className="text-amber-500/80 text-[10px] md:text-sm mt-1 font-mono uppercase tracking-wider hidden sm:block">
                  No mass production. Pure craftsmanship.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Products */}
      <div className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sidebar Filters */}
            <aside className="lg:w-64 flex-shrink-0 lg:sticky lg:top-28 lg:h-fit">
              <div className="space-y-8">
                {/* Category Filter */}
                <div>
                  <h3 className="font-mono text-sm uppercase tracking-widest text-white mb-4">
                    Category
                  </h3>
                  <div className="space-y-2">
                    <button
                      data-testid="filter-all"
                      onClick={() => setSelectedCategory('all')}
                      className={`w-full text-left px-4 py-2 font-mono text-sm transition-colors ${
                        selectedCategory === 'all'
                          ? 'text-cyan-400 bg-cyan-400/10'
                          : 'text-neutral-400 hover:text-white'
                      }`}
                    >
                      All Categories
                    </button>
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        data-testid={`filter-${cat.slug}`}
                        onClick={() => setSelectedCategory(cat.id)}
                        className={`w-full text-left px-4 py-2 font-mono text-sm transition-colors ${
                          selectedCategory === cat.id
                            ? 'text-cyan-400 bg-cyan-400/10'
                            : 'text-neutral-400 hover:text-white'
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Price Filter */}
                <div>
                  <h3 className="font-mono text-sm uppercase tracking-widest text-white mb-4">
                    Price Range
                  </h3>
                  <div className="px-2">
                    <Slider
                      data-testid="price-slider"
                      value={priceRange}
                      onValueChange={setPriceRange}
                      max={500}
                      min={0}
                      step={10}
                      className="mb-4"
                    />
                    <div className="flex justify-between text-sm text-neutral-400 font-mono">
                      <span>${priceRange[0]}</span>
                      <span>${priceRange[1]}</span>
                    </div>
                  </div>
                </div>

                {/* Sort */}
                <div>
                  <h3 className="font-mono text-sm uppercase tracking-widest text-white mb-4">
                    Sort By
                  </h3>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger
                      data-testid="sort-select"
                      className="bg-void-paper border-white/10 text-white"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-void-paper border-white/10">
                      <SelectItem value="featured">Featured</SelectItem>
                      <SelectItem value="price-low">Price: Low to High</SelectItem>
                      <SelectItem value="price-high">Price: High to Low</SelectItem>
                      <SelectItem value="name">Name: A-Z</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </aside>

            {/* Product Grid */}
            <main className="flex-1">
              <div className="flex justify-between items-center mb-8">
                <p className="text-sm text-neutral-400">
                  {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
                </p>
              </div>

              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="aspect-square bg-void-subtle animate-pulse" />
                  ))}
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-neutral-400 font-mono text-sm uppercase tracking-widest">
                    No products found
                  </p>
                  <button
                    onClick={() => {
                      setSelectedCategory('all');
                      setPriceRange([0, 500]);
                    }}
                    className="mt-4 text-cyan-400 hover:underline"
                  >
                    Clear filters
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                  {filteredProducts.map((product) => {
                    const category = categories.find(c => c.id === product.category_id);
                    return (
                      <ProductCard 
                        key={product.id} 
                        product={product} 
                        categoryName={category?.name}
                      />
                    );
                  })}
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopPage;
